import { configurationConsumer } from '@tn/configs/configurationConsumer';
import { deleteCookie, getCookie, setCookie } from 'cookies-next';

import BaseRestApi from '@/data-source-apis/base-rest-api';
import { isDevEnv } from '@/utils/getCurrentEnv';
import { getLOB } from '@/utils/server/lob-utils';
import { getRegion } from '@/utils/server/region-utils';

import { Coordinates, PositionDetails } from '../utils/configs/location';

const LAT_REGEX = /^-?([0-8]?[0-9]|90)(\.[0-9]{1,10})?$/;
const LON_REGEX = /^-?([0-9]{1,2}|1[0-7][0-9]|180)(\.[0-9]{1,10})?$/;
const IS_DEBUG_MODE = process.env.APP_DEBUG === 'true' || isDevEnv();

export class LocationApi extends BaseRestApi {
    async location(isFallback = false): Promise<PositionDetails> {
        const { req } = this.contextValue;

        const headers = new Headers(
            Object.entries(req.headers || {}).map(([key, value]) => {
                return [key, Array.isArray(value) ? value.join(', ') : (value ?? '')] as [
                    string,
                    string,
                ];
            }),
        );

        const region = getRegion(req);
        const lob = getLOB(req);
        return await getLocation(headers, isFallback, region, lob);
    }
}

/**
 * Retrieve geolocation from the request headers and return it as Coordinates.
 * Throws an error if latitude or longitude headers are missing.
 *
 * @param headers - The HTTP headers object
 * @returns Coordinates extracted from the headers
 * @throws Error if latitude or longitude headers are not provided
 */
export async function getGeoLocationFromHeaders(headers: Headers): Promise<Coordinates> {
    const latitude = headers.get('HTTP_X_CLIENT_LAT')?.toString();
    const longitude = headers.get('HTTP_X_CLIENT_LONG')?.toString();

    if (!latitude || !longitude) {
        throw new Error('Latitude and Longitude headers not provided');
    }

    return {
        latitude: Number.parseFloat(latitude),
        longitude: Number.parseFloat(longitude),
    };
}

/**
 * Prepare the full Location object with optional fallback and source fields.
 * Fallback coordinates are resolved from blueprint config per region/lob.
 *
 * @param headers - The HTTP headers object
 * @param isFallback - Indicates whether the location should be marked as a fallback
 * @param region - The region used to load the correct blueprint
 * @param lob - The line of business used to load the correct blueprint
 */
// eslint-disable-next-line max-params
export async function getLocation(
    headers: Headers,
    isFallback = false,
    region: string,
    lob: string,
): Promise<PositionDetails> {
    try {
        const coords = await getGeoLocationFromHeaders(headers);

        return prepareLocationFromPosition(coords, isFallback, 'api');
    } catch (error) {
        const latitude = await configurationConsumer.getConfigByKey<number>({
            key: 'options.geolocationFallback.latitude',
            region,
            lob,
            fallback: 0,
        });
        const longitude = await configurationConsumer.getConfigByKey<number>({
            key: 'options.geolocationFallback.longitude',
            region,
            lob,
            fallback: 0,
        });

        return prepareLocationFromPosition(
            {
                latitude: latitude ?? 0,
                longitude: longitude ?? 0,
            },
            true,
            'default',
        );
    }
}

/**
 * Validates latitude and longitude values.
 *
 * @param lat - Latitude value as a string.
 * @param lon - Longitude value as a string.
 * @returns True if both values are valid; false otherwise.
 */
export function validateLatLon(lat: string, lon: string): boolean {
    return LAT_REGEX.test(lat) && LON_REGEX.test(lon);
}

/**
 * Parses the "latlon" string and returns a `Location` object if valid.
 *
 * @param latlon - A string in the format "latitude,longitude".
 * @param isFallback - Optional. Indicates if the location is a fallback.
 * @returns A `Location` object if valid, or `null` if invalid.
 */
export function getLocationFromLatLon(
    latlon: string | null,
    isFallback = false,
): PositionDetails | null {
    if (!latlon) {
        return null;
    }

    const [lat, lon] = latlon.split(',').map((val) => val.trim());

    if (!lat || !lon) {
        return null;
    }

    if (!validateLatLon(lat, lon)) {
        return null;
    }

    return prepareLocationFromPosition(
        { latitude: Number.parseFloat(lat), longitude: Number.parseFloat(lon) },
        isFallback,
        'spoofing',
    );
}

/**
 * Retrieves a `Location` from the cookie with the given name, if available and valid.
 *
 * @param cookieName - The name of the cookie to retrieve.
 * @param isFallback - Whether to mark the returned location as a fallback value.
 * @param options - Optional cookie options forwarded to `getCookie` (e.g. `req`/`res` for SSR).
 * @returns {PositionDetails | null} A valid `Location` object if the cookie contains valid data; otherwise, `null`.
 */
export function getLocationFromCookie(
    cookieName: string,
    isFallback = false,
    options?: NonNullable<Parameters<typeof getCookie>[1]>,
): PositionDetails | null {
    const latlon = getCookie(cookieName, options) as string;
    if (!latlon) {
        return null;
    }
    const [lat, lon] = latlon.split(',').map((val) => val.trim());

    if (!validateLatLon(lat, lon)) {
        deleteCookie(cookieName, options);
        return null;
    }

    return prepareLocationFromPosition(
        { latitude: Number.parseFloat(lat), longitude: Number.parseFloat(lon) },
        isFallback,
        'cookie',
    );
}

/**
 * Saves a `Location` to a specified cookie with optional expiration time.
 *
 * @param cookieName - The name of the cookie to store the location in.
 * @param location
 * @param maxAge - Optional expiration time in seconds (defaults to 1 year).
 */
export function saveLocationToCookie(
    cookieName: string,
    location: PositionDetails,
    maxAge: number = 24 * 60 * 60,
): void {
    if (!location?.coords) {
        return;
    }
    const { latitude, longitude } = location.coords;

    if (!validateLatLon(latitude.toString(), longitude.toString())) {
        return;
    }

    const latLonString = `${latitude},${longitude}`;

    setCookie(cookieName, latLonString, {
        maxAge,
        path: '/',
    });
}

/**
 * Converts a browser `GeolocationPosition` into a `PositionDetails` object.
 * Returns `null` if the position is missing, coords are absent, or lat/lon are NaN.
 *
 * @param position - The `GeolocationPosition` returned by the browser Geolocation API.
 * @param isFallback - Whether to mark the returned location as a fallback value.
 * @returns A `PositionDetails` object, or `null` if the position is invalid.
 */
export function successfulLocationFromBrowser(
    position: GeolocationPosition,
    isFallback = false,
): PositionDetails | null {
    if (!position?.coords) {
        return null;
    }

    const { latitude, longitude } = position.coords;

    if (
        latitude === null ||
        latitude === undefined ||
        longitude === null ||
        longitude === undefined ||
        Number.isNaN(latitude) ||
        Number.isNaN(longitude)
    ) {
        return null;
    }

    const coords = position.coords as Coordinates;
    return prepareLocationFromPosition(coords, isFallback, 'browser');
}

/**
 * Prepares a `Location` object from the provided coordinates with additional optional parameters.
 *
 * @param {Coordinates} coords - The geographical coordinates to be used for the location.
 * @param {boolean} [isFallback=false] - Indicates whether the location is a fallback value.
 * @param {string} [source='unknown'] - The origin or source of the location data, used when debugging is enabled.
 * @returns {PositionDetails} A `Location` object containing the specified coordinates, fallback status,
 *                     and optionally the source information if debug mode is active.
 */
// Round to 3 decimal places (~85–111 m at CONUS latitudes) to prevent cache-key churn
// from GPS jitter below the DMA-change threshold.
const COORD_PRECISION = 1000;
const roundCoord = (n: number): number => Math.round(n * COORD_PRECISION) / COORD_PRECISION;

export const prepareLocationFromPosition = (
    coords: Coordinates,
    isFallback = false,
    source = 'unknown',
): PositionDetails => {
    return {
        coords: {
            ...coords,
            latitude: roundCoord(coords.latitude),
            longitude: roundCoord(coords.longitude),
        },
        isFallback,
        ...(IS_DEBUG_MODE && { source }),
    } as PositionDetails;
};
